/**
 * AdminPatterns — Operational Memory + Pattern Recognition V1 (May 25, 2026).
 *
 * Third intelligence layer of the platform:
 *   Layer 1 (truth):       Needs Attention
 *   Layer 2 (orientation): Contextual Guidance
 *   Layer 3 (THIS):        Patterns & Trends
 *
 * Doctrine (operator-defined):
 *   • Calm, paginated, operationally useful.
 *   • What KEEPS happening, not what is happening right now.
 *   • Rule-based; trend direction by 3-scan slope.
 *   • Admin-only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, AlertCircle, Info, CheckCircle2, RefreshCcw, Crown,
  ChevronRight, Clock, TrendingUp, TrendingDown, Minus, ExternalLink, ShieldCheck,
  Network, Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: { label: "Critical", icon: AlertTriangle, cls: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700" },
  high:     { label: "High",     icon: AlertCircle,  cls: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700" },
  medium:   { label: "Medium",   icon: Info,         cls: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700" },
  low:      { label: "Low",      icon: Info,         cls: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" },
};

const STATUS = {
  emerging:    { label: "Emerging",    cls: "bg-sky-50 text-sky-800 border-sky-200" },
  active:      { label: "Active",      cls: "bg-amber-50 text-amber-800 border-amber-200" },
  worsening:   { label: "Worsening",   cls: "bg-rose-50 text-rose-800 border-rose-200" },
  stabilising: { label: "Stabilising", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  resolved:    { label: "Resolved",    cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus };
const TREND_CLS  = {
  up:   "text-rose-600 dark:text-rose-400",
  down: "text-emerald-600 dark:text-emerald-400",
  flat: "text-slate-400",
};

const PATTERN_TYPE_LABEL = {
  repeated_review_failures:      "Repeated review failures",
  repeated_lesson_cancellations: "Repeated lesson cancellations",
  dormant_learner:               "Dormant learner",
  stalled_progression:           "Stalled progression",
  repeated_unresolved_issues:    "Repeated unresolved issues",
  repeated_debrief_skip:         "Repeated debrief skipped",
  repeated_payment_failures:     "Repeated payment failures",
  chronic_onboarding:            "Chronic onboarding",
  recurring_schedule_gaps:       "Recurring schedule gaps",
  workmode_interruptions:        "Work Mode interruptions",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function Sparkline({ history = [] }) {
  if (!history || history.length === 0) {
    return <span className="text-[10px] text-slate-400">no history</span>;
  }
  const max = Math.max(...history, 1);
  return (
    <div className="flex items-end gap-0.5 h-5" aria-hidden="true">
      {history.map((v, i) => (
        <span
          key={i}
          className="w-1.5 bg-slate-300 dark:bg-slate-600 rounded-sm"
          style={{ height: `${Math.max(8, (v / max) * 100)}%` }}
        />
      ))}
    </div>
  );
}

/**
 * TraceImpactPanel — calm inline panel showing which guidance items
 * currently cite this pattern via the admin debug surface
 * (/api/guidance/today?debug=1).
 *
 * The panel is read-only and never persists state — re-tracing
 * always produces the live answer for the current pattern set.
 */
function TraceImpactPanel({ pattern, trace }) {
  if (trace.error) {
    return (
      <div
        data-testid={`patterns-trace-error-${pattern.id}`}
        className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-[12px] text-rose-700 dark:text-rose-300"
      >
        {trace.error}
      </div>
    );
  }
  if (!trace.matches || trace.matches.length === 0) {
    return (
      <div
        data-testid={`patterns-trace-empty-${pattern.id}`}
        className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-3 text-[12.5px] text-slate-600 dark:text-slate-300"
      >
        <p className="font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1.5 mb-1">
          <Network className="w-3.5 h-3.5" /> No active guidance currently fires from this pattern.
        </p>
        <p className="leading-snug">
          This pattern is recorded in operational memory but no live guidance rule
          cites it right now. That can happen when: the pattern is acknowledged or
          resolved, no relevant role surface fetches today, or no rule consumes
          this pattern type.
        </p>
      </div>
    );
  }
  return (
    <div
      data-testid={`patterns-trace-panel-${pattern.id}`}
      className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 px-3 py-3 space-y-3"
    >
      <p className="font-bold text-[12px] text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
        <Network className="w-3.5 h-3.5" />
        {trace.matches.length} guidance item{trace.matches.length === 1 ? "" : "s"} cite this pattern
      </p>
      <ul className="space-y-2">
        {trace.matches.map((m, idx) => {
          const it = m.item || {};
          const expl = it._explain || {};
          return (
            <li
              key={`${it.dedup_key}-${idx}`}
              data-testid={`patterns-trace-row-${pattern.id}-${idx}`}
              className="rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2 text-[12.5px]"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
                  {m.role}
                </span>
                <span className="font-bold text-slate-900 dark:text-white truncate">{it.title}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  {it.severity} · pri {it.priority}
                </span>
              </div>
              {it.why && (
                <p className="mt-1 text-slate-600 dark:text-slate-300 leading-snug">{it.why}</p>
              )}
              <p className="mt-1.5 text-[10px] font-mono text-slate-400">
                rule={it.rule_id || expl.source} · dedup={it.dedup_key}
                {expl.rollup ? " · rollup" : " · single"}
                {m.matched_by_id ? " · matched by pattern_id" : " · matched by pattern_type"}
              </p>
            </li>
          );
        })}
      </ul>
      <p className="text-[10px] text-slate-500 font-mono">
        explain.source = patterns · pattern_id = {pattern.id} · type = {pattern.pattern_type}
      </p>
    </div>
  );
}

export default function AdminPatterns() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ by_status: {}, by_severity: {}, open_total: 0 });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterType, setFilterType] = useState("");
  const [offset, setOffset] = useState(0);
  // Trace guidance impact — per-pattern lazy fetch + cache.
  // Shape: { [patternId]: { loading, matches: [{ role, item }], error } }
  const [traces, setTraces] = useState({});
  const PAGE = 50;

  const traceImpact = useCallback(async (pattern) => {
    const pid = pattern.id;
    const ptype = pattern.pattern_type;
    setTraces((prev) => ({ ...prev, [pid]: { loading: true, matches: [], error: null } }));
    try {
      // Fetch BOTH role surfaces — pattern impact may land in either.
      // Server enforces admin-only access; ?debug=1 honoured only for admins.
      const [adminResp, instructorResp] = await Promise.all([
        axios.get(`${API}/api/guidance/today?role=admin&debug=1`, { withCredentials: true }),
        axios.get(`${API}/api/guidance/today?role=instructor&debug=1`, { withCredentials: true }),
      ]);
      const matches = [];
      const checkSurface = (data, role) => {
        if (!data?.debug_explain) return;  // server denied debug surface
        for (const it of data.items || []) {
          const expl = it?._explain;
          if (!expl || expl.source !== "patterns") continue;
          const ids = expl.pattern_ids || [];
          const types = expl.pattern_types || [];
          if (ids.includes(pid) || types.includes(ptype)) {
            matches.push({ role, item: it, matched_by_id: ids.includes(pid) });
          }
        }
      };
      checkSurface(adminResp.data, "admin");
      checkSurface(instructorResp.data, "instructor");
      setTraces((prev) => ({ ...prev, [pid]: { loading: false, matches, error: null } }));
    } catch (e) {
      setTraces((prev) => ({
        ...prev,
        [pid]: { loading: false, matches: [], error: "Couldn't trace guidance impact" },
      }));
      toast.error("Couldn't trace guidance impact");
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterSeverity) params.set("severity", filterSeverity);
      if (filterType) params.set("pattern_type", filterType);
      params.set("limit", String(PAGE));
      params.set("offset", String(offset));
      const [list, c] = await Promise.all([
        axios.get(`${API}/api/admin/patterns?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/admin/patterns/counts`, { withCredentials: true }),
      ]);
      setItems(list.data?.items || []);
      setCounts(c.data || { by_status: {}, by_severity: {}, open_total: 0 });
    } catch {
      toast.error("Couldn't load patterns.");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity, filterType, offset]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/api/admin/patterns/scan`, {}, { withCredentials: true });
      toast.success(
        `Scan complete · ${r.data?.total_patterns ?? 0} patterns across ${r.data?.detector_results?.length ?? 0} detectors`,
      );
      await load();
    } catch {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const acknowledge = async (id) => {
    try {
      await axios.post(`${API}/api/admin/patterns/${id}/acknowledge`, {}, { withCredentials: true });
      toast.success("Pattern acknowledged");
      await load();
    } catch {
      toast.error("Couldn't acknowledge pattern");
    }
  };

  const sevCount = (s) => counts.by_severity?.[s] || 0;
  const statCount = (s) => counts.by_status?.[s] || 0;

  const patternTypes = useMemo(
    () => Object.keys(PATTERN_TYPE_LABEL),
    [],
  );

  return (
    <div data-testid="admin-patterns" className="min-h-dvh bg-slate-50 dark:bg-slate-950 pb-12">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/admin" className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm">← Admin</Link>
          <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <Crown className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">Patterns &amp; Trends</p>
            <p className="text-[11px] text-slate-500 leading-tight">Operational memory · what KEEPS happening · rule-based · admin-only</p>
          </div>
          <button
            type="button"
            data-testid="patterns-scan-button"
            onClick={runScan}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Run scan now"}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-5 space-y-4">
        {/* Status summary row — operational memory at a glance */}
        <div data-testid="patterns-status-summary" className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {["emerging", "active", "worsening", "stabilising", "resolved"].map((st) => (
            <button
              key={st}
              type="button"
              onClick={() => { setOffset(0); setFilterStatus(filterStatus === st ? "" : st); }}
              data-testid={`patterns-status-${st}`}
              className={`rounded-lg border px-3 py-3 text-left transition ${
                filterStatus === st ? "ring-2 ring-offset-1 ring-indigo-400 dark:ring-offset-slate-950" : ""
              } ${STATUS[st].cls}`}
            >
              <div className="text-[10.5px] uppercase tracking-widest font-bold opacity-80">{STATUS[st].label}</div>
              <div className="text-2xl font-extrabold mt-1 tabular-nums">{statCount(st)}</div>
            </button>
          ))}
        </div>

        {/* Severity summary row */}
        <div data-testid="patterns-severity-summary" className="grid grid-cols-4 gap-2">
          {["critical", "high", "medium", "low"].map((s) => {
            const meta = SEVERITY[s];
            const Icon = meta.icon;
            return (
              <button
                key={s}
                type="button"
                onClick={() => { setOffset(0); setFilterSeverity(filterSeverity === s ? "" : s); }}
                data-testid={`patterns-sev-${s}`}
                className={`rounded-lg border px-3 py-2 text-left transition ${
                  filterSeverity === s ? "ring-2 ring-offset-1 ring-slate-400 dark:ring-offset-slate-950" : ""
                } ${meta.cls}`}
              >
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest font-bold opacity-80">
                  <Icon className="w-3 h-3" />{meta.label}
                </div>
                <div className="text-xl font-extrabold mt-0.5 tabular-nums">{sevCount(s)}</div>
              </button>
            );
          })}
        </div>

        {/* Pattern type filter */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 shrink-0">Pattern:</span>
          <select
            data-testid="patterns-type-filter"
            value={filterType}
            onChange={(e) => { setOffset(0); setFilterType(e.target.value); }}
            className="flex-1 sm:flex-none rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-slate-700 dark:text-slate-200"
          >
            <option value="">All types</option>
            {patternTypes.map((t) => (
              <option key={t} value={t}>{PATTERN_TYPE_LABEL[t]}</option>
            ))}
          </select>
          {(filterStatus || filterSeverity || filterType) && (
            <button
              type="button"
              data-testid="patterns-clear-filters"
              onClick={() => { setOffset(0); setFilterStatus(""); setFilterSeverity(""); setFilterType(""); }}
              className="ml-auto px-2.5 py-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Pattern list */}
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="patterns-empty" className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">No patterns to show.</p>
            <p className="text-xs text-slate-500 mt-1">Either nothing is recurring, or your filters are narrow.</p>
          </div>
        ) : (
          <ul data-testid="patterns-list" className="space-y-2">
            {items.map((p) => {
              const sev = SEVERITY[p.severity] || SEVERITY.low;
              const stat = STATUS[p.status] || STATUS.emerging;
              const SevIcon = sev.icon;
              const TrendIcon = TREND_ICON[p.trend_direction] || Minus;
              const trendCls = TREND_CLS[p.trend_direction] || TREND_CLS.flat;
              const isOpen = expanded === p.id;
              return (
                <li
                  key={p.id}
                  data-testid={`patterns-row-${p.id}`}
                  className={`rounded-xl bg-white dark:bg-slate-900 border overflow-hidden ${
                    p.operator_acknowledged
                      ? "border-slate-200 dark:border-slate-800 opacity-75"
                      : "border-slate-200 dark:border-slate-800"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : p.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${sev.cls}`}>
                      <SevIcon className="w-2.5 h-2.5" />{sev.label}
                    </span>
                    <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${stat.cls}`}>
                      {stat.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                        {PATTERN_TYPE_LABEL[p.pattern_type] || p.pattern_type}
                      </p>
                      <p className="text-[11.5px] text-slate-500 truncate">
                        {p.affected?.label || p.pattern_type} · count {p.occurrence_count} · <Clock className="inline w-3 h-3 -mt-px" /> {relativeTime(p.last_seen_at)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-xs font-bold tabular-nums ${trendCls}`}>
                      <TrendIcon className="w-4 h-4" />
                      <span className="tabular-nums">{p.occurrence_count}</span>
                    </span>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition ${isOpen ? "rotate-90" : ""}`} />
                  </button>

                  {isOpen && (
                    <div data-testid={`patterns-detail-${p.id}`} className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30">
                      <div className="grid sm:grid-cols-2 gap-3 text-[12.5px]">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Evidence</p>
                          <p className="text-slate-700 dark:text-slate-300 leading-snug">{p.evidence_summary || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Suggested intervention</p>
                          <p className="text-slate-700 dark:text-slate-300 leading-snug">{p.suggested_intervention || "—"}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-[11.5px] text-slate-500">
                        <span className="font-bold uppercase tracking-widest text-[10px]">Scan history</span>
                        <Sparkline history={p.history || []} />
                        <span className="tabular-nums">[{(p.history || []).join(", ")}]</span>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        {p.affected?.route && (
                          <Link
                            to={p.affected.route}
                            data-testid={`patterns-open-${p.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Open {p.affected.kind}
                          </Link>
                        )}
                        {!p.operator_acknowledged && (
                          <button
                            type="button"
                            data-testid={`patterns-acknowledge-${p.id}`}
                            onClick={() => acknowledge(p.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100"
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />Acknowledge
                          </button>
                        )}
                        {p.operator_acknowledged && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-500">
                            <ShieldCheck className="w-3.5 h-3.5" />Acknowledged
                          </span>
                        )}
                        <button
                          type="button"
                          data-testid={`patterns-trace-${p.id}`}
                          onClick={() => traceImpact(p)}
                          disabled={traces[p.id]?.loading}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                        >
                          {traces[p.id]?.loading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Network className="w-3.5 h-3.5" />
                          )}
                          {traces[p.id]?.loading ? "Tracing…" : "Trace guidance impact"}
                        </button>
                      </div>

                      {/* Trace guidance impact panel */}
                      {traces[p.id] && !traces[p.id].loading && (
                        <TraceImpactPanel
                          pattern={p}
                          trace={traces[p.id]}
                        />
                      )}

                      <p className="text-[10px] text-slate-400 font-mono">
                        type={p.pattern_type} · trend={p.trend_direction} · first_seen={p.first_seen_at?.slice(0, 19)}
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Pagination */}
        {(items.length === PAGE || offset > 0) && (
          <div className="flex items-center justify-between pt-2 text-xs">
            <button
              type="button"
              data-testid="patterns-prev"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
              className="px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 disabled:opacity-40"
            >
              ← Newer
            </button>
            <span className="text-slate-500">
              Showing {offset + 1}–{offset + items.length}
            </span>
            <button
              type="button"
              data-testid="patterns-next"
              disabled={items.length < PAGE}
              onClick={() => setOffset(offset + PAGE)}
              className="px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 disabled:opacity-40"
            >
              Older →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
